import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, Input } from '@/design-system'
import { passwordIssueLabels, passwordIssues } from '@/features/employees/domain/temporaryPassword'
import { AuthFailure } from '../domain/auth'
import { AuthLayout } from './AuthLayout'
import { useAuth } from './useAuth'

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Saisissez le mot de passe temporaire reçu.'),
    password: z.string().superRefine((value, context) => {
      const [issue] = passwordIssues(value)
      if (issue) context.addIssue({ code: 'custom', message: passwordIssueLabels[issue] })
    }),
    confirmation: z.string(),
  })
  .refine((values) => values.password === values.confirmation, {
    path: ['confirmation'],
    message: 'Les mots de passe ne correspondent pas.',
  })
  .refine((values) => values.password !== values.currentPassword, {
    path: ['password'],
    message: 'Le nouveau mot de passe doit être différent du mot de passe temporaire.',
  })
type FormValues = z.infer<typeof schema>

/**
 * Première connexion avec un mot de passe temporaire : aucun module n'est accessible avant son remplacement.
 * L'obligation est appliquée par le serveur (aucune permission accordée); cette page n'en est que l'issue.
 */
export function ForcePasswordChangePage() {
  const { session, replaceTemporaryPassword, signOut } = useAuth()
  const [formError, setFormError] = useState<string>()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const submit = handleSubmit(async ({ currentPassword, password }) => {
    setFormError(undefined)
    try {
      await replaceTemporaryPassword(currentPassword, password)
    } catch (error) {
      setFormError(error instanceof AuthFailure ? error.message : 'Le mot de passe n’a pas pu être remplacé.')
    }
  })

  return (
    <AuthLayout>
      <div className="auth-card__heading">
        <span className="auth-page__eyebrow">Première connexion</span>
        <h2>Choisissez votre mot de passe</h2>
        <p>
          Le mot de passe temporaire remis par l’administrateur doit être remplacé avant d’accéder à RECA
          {session ? ` (${session.email})` : ''}.
        </p>
      </div>
      <form
        className="auth-form"
        onSubmit={(event) => {
          void submit(event)
        }}
      >
        <Input
          label="Mot de passe temporaire"
          type="password"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />
        <Input
          label="Nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          hint="Au moins 12 caractères et trois types de caractères parmi : minuscules, majuscules, chiffres et symboles."
          error={errors.password?.message}
          {...register('password')}
        />
        <Input
          label="Confirmer le nouveau mot de passe"
          type="password"
          autoComplete="new-password"
          error={errors.confirmation?.message}
          {...register('confirmation')}
        />
        {formError ? (
          <div className="auth-form__error" role="alert">
            {formError}
          </div>
        ) : null}
        <Button type="submit" variant="primary" size="large" icon={KeyRound} loading={isSubmitting}>
          Enregistrer et continuer
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            void signOut()
          }}
        >
          Se déconnecter
        </Button>
      </form>
    </AuthLayout>
  )
}
